<template>
  <div class="page-listing-search">
    <h1 class="sr-only">
      Search House And Land Package, Designs, Showhomes and more
    </h1>
    <ListingSearchResults />
  </div>
</template>

<script lang="ts" setup>
import {useListingStore} from '~/store/listing';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';
import {ListingData, ListingHits} from '~/@types/listing';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const listingStore = useListingStore();
const route = useRoute();
const {$listing} = useNuxtApp();
const hasMounted = ref(false);

/**
 * @_SERVER_PREFETCH Hook
 */
onServerPrefetch(async () => {
  listingStore.setCompanySlug(null);
  await setListingDataResponse();
});

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const title = 'Find Listings | New Homes - Search House And Land Package, Designs, Showhomes and more in New Zealand';
const description = 'Find your dream home in New Zealand with New Homes. Search listings by name, location, or type, and tailor your results with filters for bedrooms, bathrooms, and budget. Start your home buying journey with us today.';
useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
});

/**
 * @_MOUNTED Hook
 */
onMounted(async () => {
  listingStore.setCompanySlug(null);
  nextTick(() => {
    hasMounted.value = true;
  });

  listingStore.setIsLoading(true);
  if (listingStore.listingData.found === 0 || ListingSearchQueryHandler.isCurrentParamsDifferentToFetchedParams()) {
    setListingDataResponse();
  } else {
    listingStore.setIsLoading(false);
  }
});

/**
 * @_WATCH route.query
 */
watch(() => route.query, async () => {
  if (ListingSearchQueryHandler.isCurrentParamsDifferentToFetchedParams()) {
    await setListingDataResponse();
  }
});

/**
 * Set the listing data
 */
async function setListingDataResponse () {
  listingStore.setIsLoading(true);
  listingStore.setLastFetchedParams(getCurrentParamsJsonString());
  const listingResultResponse = await $listing.search() as ListingData;
  if (!listingResultResponse) return;
  listingResultResponse.hits = reorderHitsWithAds(listingResultResponse.hits);
  listingStore.setListingData(listingResultResponse);
  listingStore.setIsLoading(false);
}

/**
 * Reorder the hits with ads
 *
 * @param hits
 */
function reorderHitsWithAds(hits: ListingHits[]): ListingHits[] {
  // Separate the ads and regular listings
  const ads = hits.filter(hit => hit.document.category === 'Ad');
  const regularListings = hits.filter(hit => hit.document.category !== 'Ad');

  // Function to insert an item at a specific index
  const insertAt = (arr: ListingHits[], index: number, newItem: ListingHits) => {
    return [...arr.slice(0, index), newItem, ...arr.slice(index)];
  };

  // Initialize the new hits array with regular listings
  let newHits = [...regularListings];

  // Place the first ad at the 3rd position
  if (ads.length > 0) {
    newHits = insertAt(newHits, 2, ads[0]);
  }

  // Place the remaining ads after every 6 items
  for (let i = 1; i < ads.length; i++) {
    let position = 2 + i * 6; // Calculate the position for each ad
    if (position < newHits.length) {
      newHits = insertAt(newHits, position, ads[i]);
    } else {
      // If the calculated position is beyond the array length, add to the end
      newHits.push(ads[i]);
    }
  }

  return newHits;
}


/**
 * Get the last fetched params
 */
function getCurrentParamsJsonString () {
  return JSON.stringify(route.query) + JSON.stringify(listingStore.companySlug);
}
</script>
